/**
 * active-domain-store.ts — the persisted active-domain pointer, keyed PER ACCOUNT.
 *
 * The defect this fixes (15 July 2026 incident). ADR-097 persisted the active
 * domain under ONE global localStorage key, `cji-home-domain`, shared by every
 * account and every context the operator could reach. The provider writes that key
 * whenever a token resolves, so simply VIEWING another context's dashboard —
 * a partner/beta impersonation, a consultant client — overwrote the pointer for
 * the operator's OWN account. Coming back, the stale token failed the
 * `domains.some(...)` guard and the resolver fell to `domains[0]`, which is
 * whichever domain's token happens to sort first (see resolveActiveToken).
 *
 * One key cannot hold N contexts' choices. The pointer is now keyed by accountId,
 * so a context's choice is scoped to that context by construction: writing one
 * account's pointer can no longer perturb another's, and no teardown ordering has
 * to be right for isolation to hold.
 *
 * Sign-out additionally purges every pointer (clearPortalSessionState) — the
 * active-domain selection dies with the sign-in it belonged to, exactly as the
 * impersonation session and context markers already do.
 */

/** Key prefix for the per-account pointer: `cji-home-domain:<accountId>`.
 *  NOTE this prefix is ALSO the pre-fix global key (`cji-home-domain`), so a
 *  prefix sweep purges the legacy poisoned pointer too — see clearActiveDomainPointers. */
export const ACTIVE_DOMAIN_KEY_PREFIX = 'cji-home-domain';

/** The persisted-pointer key for one account. */
export function activeDomainKey(accountId: string): string {
  return `${ACTIVE_DOMAIN_KEY_PREFIX}:${accountId}`;
}

/** Read the last-viewed domain for ONE account (null when unset/unavailable). */
export function readActiveDomain(accountId: string | undefined): string | null {
  if (!accountId) return null;
  try {
    return localStorage.getItem(activeDomainKey(accountId));
  } catch {
    return null;
  }
}

/** Persist the last-viewed domain for ONE account. */
export function writeActiveDomain(accountId: string | undefined, token: string): void {
  if (!accountId) return;
  try {
    localStorage.setItem(activeDomainKey(accountId), token);
  } catch {
    /* storage unavailable — the URL still carries the choice */
  }
}

/**
 * Purge every persisted active-domain pointer, including the pre-fix global
 * `cji-home-domain` key (the prefix sweep catches it). Called at sign-out.
 * Idempotent and safe in private mode.
 */
export function clearActiveDomainPointers(): void {
  try {
    const doomed: string[] = [];
    for (let i = 0; i < localStorage.length; i += 1) {
      const key = localStorage.key(i);
      if (key && key.startsWith(ACTIVE_DOMAIN_KEY_PREFIX)) doomed.push(key);
    }
    for (const key of doomed) localStorage.removeItem(key);
  } catch {
    /* private mode — nothing stored, nothing to clear */
  }
}
